import axios from 'axios';
import HttpStatus from 'http-status-codes';
import jwt_decode from 'jwt-decode';
import { datadogRum } from '@datadog/browser-rum';
import axiosRetry from 'axios-retry';

// Retries are only used when the "withRetry" param is true for each HTTP method.
const axiosRetryConfig = { retries: 3, retryDelay: () => 1000 };
const JWT_EXPIRATION_LEEWAY_MS = 5 * 1000;

const getCustomerIdFromRoute = () => {
  const routePath = window.location?.pathname || '';
  const customerIdMatch = routePath.match(/\/customers\/(\d+)(?:\/|$)/i);
  return customerIdMatch ? customerIdMatch[1] : undefined;
};

export class HTTP {
  constructor(
    axiosConfig = {
      baseURL: '/api',
      withCredentials: true,
    },
    useArborService = true,
    authorizationConfig = {},
  ) {
    this.instance = axios.create(axiosConfig);
    this.useArborService = useArborService;
    this.authorizationConfig = authorizationConfig;
    this.refreshPromise = null;

    // Default the retries to 0 (don't retry by default)
    axiosRetry(this.instance, { retries: 0 });

    this.instance.interceptors.request.use(async config => {
      if (this.useArborService && !config?.skipAuthRefresh) {
        await this.ensureValidJwt();
      }

      if (this.useArborService) {
        const jwtToken = window.sessionStorage.getItem('id_token');
        const customerIdFromRoute = getCustomerIdFromRoute();

        if (jwtToken) {
          config.headers = {
            ...(config.headers || {}),
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': config.headers?.['Content-Type'] || 'application/json',
          };

          if (customerIdFromRoute) {
            config.headers['x-customer-id'] = customerIdFromRoute;
          }
        }
      }

      return config;
    });

    // configure interceptor
    this.instance.interceptors.response.use(
      response => {
        const jwt = response.headers?.authorization?.split(' ')?.[1];
        if (jwt) {
          window.sessionStorage.setItem('id_token', jwt);
          this.authorizationConfig.headers = {
            ...(this.authorizationConfig.headers || {}),
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          };

          const decoded = jwt_decode(jwt);
          datadogRum?.setUser({
            id: decoded.id,
            name: decoded.name,
            email: decoded.sub,
            customer_id: decoded.customer_id,
          });
        } else {
          datadogRum?.clearUser();
        }

        return response;
      },
      error => {
        if (error && error.response && error.response.status === HttpStatus.UNAUTHORIZED) {
          this.redirectToAuthPage();
        }
        throw error;
      },
    );

    // Configure authorization
    if (useArborService) {
      this.authorizationConfig.headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${window.sessionStorage.getItem('id_token')}`,
      };
    }
  }

  redirectToAuthPage = () => {
    window.sessionStorage.removeItem('id_token');
    window.location.href = `/auth.html?returnUrl=${encodeURIComponent(window.location)}`;
  };

  isJwtExpired = jwtToken => {
    try {
      const decoded = jwt_decode(jwtToken);
      const expirationInMs = Number(decoded.exp) * 1000;
      return !expirationInMs || Date.now() >= expirationInMs - JWT_EXPIRATION_LEEWAY_MS;
    } catch (_error) {
      return true;
    }
  };

  ensureValidJwt = async () => {
    const jwtToken = window.sessionStorage.getItem('id_token');
    if (jwtToken && !this.isJwtExpired(jwtToken)) {
      return;
    }

    await this.refreshJwt();
  };

  refreshJwt = async () => {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.instance
      .post('/auth/refresh', {}, { skipAuthRefresh: true })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  };

  get = (url, config, withRetry = false) => {
    const retryConfig = withRetry ? { 'axios-retry': axiosRetryConfig } : {};
    const requestConfig = { ...this.authorizationConfig, ...retryConfig, ...config };
    return this.instance.get(url, requestConfig);
  };

  post = (url, data, config, withRetry = false) => {
    const retryConfig = withRetry ? { 'axios-retry': axiosRetryConfig } : {};
    const requestConfig = { ...this.authorizationConfig, ...retryConfig, ...config };
    return this.instance.post(url, data, requestConfig);
  };

  put = (url, data, config, withRetry = false) => {
    const retryConfig = withRetry ? { 'axios-retry': axiosRetryConfig } : {};
    const requestConfig = { ...this.authorizationConfig, ...retryConfig, ...config };
    return this.instance.put(url, data, requestConfig);
  };

  patch = (url, data, config, withRetry = false) => {
    const retryConfig = withRetry ? { 'axios-retry': axiosRetryConfig } : {};
    const requestConfig = { ...this.authorizationConfig, ...retryConfig, ...config };
    return this.instance.patch(url, data, requestConfig);
  };

  delete = (url, config, withRetry = false) => {
    const retryConfig = withRetry ? { 'axios-retry': axiosRetryConfig } : {};
    const requestConfig = { ...this.authorizationConfig, ...retryConfig, ...config };
    return this.instance.delete(url, requestConfig);
  };
}

export default new HTTP();
